import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Helmet } from "react-helmet";
import SchemaMarkup from "@/components/SchemaMarkup";
import Breadcrumb from "@/components/Breadcrumb";
import { createArticleSchema } from "@/utils/schemaHelpers";
import HoroscopeContent from "@/components/HoroscopeContent";

const HoroscopoLibra = () => {
  const articleSchema = createArticleSchema(
    "Libra: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Descubre el horóscopo diario, semanal y mensual de Libra. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 23 de septiembre y el 22 de octubre.",
    `${window.location.origin}/horoscopo-libra`
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Horóscopo Libra",
        "item": `${window.location.origin}/horoscopo-libra`
      }
    ]
  };

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Horóscopo", url: "/horoscopo" },
    { name: "Libra" }
  ];

  return (
    <>
      <Helmet>
        <title>Libra: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Descubre el horóscopo diario, semanal y mensual de Libra. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 23 de septiembre y el 22 de octubre." />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-libra`} />
        <meta property="og:title" content="Libra: Horóscopo Diario, Características y Compatibilidad" />
        <meta property="og:description" content="Descubre el horóscopo diario, semanal y mensual de Libra. Predicciones de amor, trabajo, salud y dinero." />
        <meta property="og:url" content={`${window.location.origin}/horoscopo-libra`} />
        <meta property="og:type" content="article" />
      </Helmet>
      
      <SchemaMarkup schema={articleSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Breadcrumb items={breadcrumbItems} />
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">♎</div>
              <h1 className="text-2xl font-bold mb-4 text-glow">
                Horóscopo de Libra
              </h1>
              <p className="text-xl text-muted-foreground">23 de Septiembre - 22 de Octubre</p>
            </div>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-primary">Tu Horóscopo Libra: Predicciones Actualizadas</h2>
              <HoroscopeContent sign="libra" signName="Libra" />
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-secondary">Características Profundas del Signo Libra: El Balanza y el Juez</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Los Pilares Astrológicos de Libra</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-base mb-2 text-primary">💨 Elemento: Aire</h4>
                    <p className="text-muted-foreground">Enfoque en las relaciones, la justicia y la estética. Libra vive en el mundo de las ideas y las conexiones.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-secondary">⚖️ Modalidad: Cardinal</h4>
                    <p className="text-muted-foreground">Define la necesidad de iniciar la armonía y el compromiso. Activo en crear balance.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-accent">♀️ Planeta Regente: Venus</h4>
                    <p className="text-muted-foreground">Rige el Equilibrio, la Diplomacia y la Belleza. El aspecto social y artístico de Venus.</p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Perfil de Personalidad Libra: Fortalezas y Debilidades</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-bold text-base mb-3 text-primary">✨ Fortalezas</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Sociabilidad:</strong> Facilidad natural para conectar</li>
                      <li>• <strong>Diplomacia:</strong> Maestro de la mediación</li>
                      <li>• <strong>Justicia:</strong> Sentido imparcial del bien y el mal</li>
                      <li>• <strong>Sentido Estético:</strong> Apreciación profunda de la belleza</li>
                      <li>• <strong>Encanto:</strong> Magnetismo personal elegante</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-3 text-secondary">⚠️ Debilidades</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Indecisión:</strong> Dificultad para tomar decisiones</li>
                      <li>• <strong>Dependencia:</strong> Necesita validación de pareja</li>
                      <li>• <strong>Superficialidad:</strong> Énfasis excesivo en apariencias</li>
                      <li>• <strong>Evitar conflicto:</strong> Dificultad con confrontación</li>
                      <li>• <strong>Complacencia:</strong> Sacrifica necesidades propias</li>
                    </ul>
                  </div>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-lg font-bold text-primary">Mantra Clave: "Yo Equilibro"</p>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Libra en el Amor: La Búsqueda Constante de la Pareja Ideal</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Libra es el signo más orientado a las relaciones del zodiaco. Busca la pareja perfecta con quien compartir una vida de belleza, armonía y equilibrio. El romance para Libra debe ser elegante, refinado y lleno de consideración mutua. Valora la estética del amor tanto como la emoción.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Necesita una relación donde exista igualdad, respeto y apreciación mutua. Puede perder su identidad en la pareja si no tiene cuidado. Su desafío es encontrar el balance entre complacer a su pareja y mantener su propia autonomía. Cuando está en equilibrio, es el compañero más considerado y armonioso.
                </p>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Libra en el Trabajo y Finanzas: Mediación y Asuntos Legales</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Libra destaca en derecho, mediación, relaciones públicas, diseño, moda, arte, decoración y cualquier campo que requiera sentido estético y habilidad diplomática. Su capacidad para ver múltiples perspectivas lo hace invaluable en negociaciones y resolución de conflictos.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Con las finanzas, Libra aprecia el lujo y la calidad, pero también valora la justicia económica. Puede gastar en belleza y experiencias sociales. Excelente para negocios relacionados con arte, lujo y servicios que requieran elegancia y buen gusto.
                </p>
              </Card>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-6 text-accent">Compatibilidad de Libra con los Otros Signos Zodiacales</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">💚 Tu Match Perfecto</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-base mb-2">♊ Géminis y ♒ Acuario</h4>
                    <p className="text-muted-foreground">
                      Los signos de aire comparten amor por las ideas y la comunicación refinada. Con Géminis hay diversión intelectual constante, mientras que con Acuario existe una visión compartida de justicia social y progreso.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2">♌ Leo y ♐ Sagitario</h4>
                    <p className="text-muted-foreground">
                      Los signos de fuego aportan la pasión que complementa la naturaleza romántica de Libra. Leo trae glamour y drama, mientras que Sagitario añade aventura y filosofía a la relación.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">⚡ Los Desafíos y Lecciones de Libra</h3>
                <p className="text-muted-foreground mb-4">
                  El valor de su propia decisión es el gran desafío de Libra. Aries, su signo opuesto, le enseña sobre acción independiente y la valentía de decidir sin buscar aprobación externa. Los signos de agua (Cáncer, Escorpio, Piscis) lo desafían con profundidad emocional.
                </p>
                <p className="text-muted-foreground">
                  Los signos de tierra (Tauro, Virgo, Capricornio) pueden frustrar a Libra con su enfoque práctico sobre la estética, pero le enseñan sobre la importancia de la sustancia sobre el estilo y la necesidad de tomar decisiones firmes.
                </p>
              </Card>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default HoroscopoLibra;
